/**
 * Catálogo da API para desenvolvedores.
 *
 * Fonte única de três coisas: a página `/desenvolvedores`, o `openapi.json`
 * gerado no build (`prerenderizar.mjs`) e o teste e2e `test/docs-api.e2e-spec.ts`,
 * que chama cada rota daqui e falha se alguma não existir no servidor. Rota
 * nova na API pública? Entra aqui — e só aqui.
 *
 * Sem imports: o teste do backend lê este arquivo direto.
 */

export type Metodo = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface Parametro {
  nome: string;
  em: 'path' | 'query' | 'body' | 'form';
  tipo: 'string' | 'integer' | 'boolean' | 'array' | 'file';
  obrigatorio?: boolean;
  descricao: string;
  /** Valor usado no exemplo e no teste de existência da rota. */
  exemplo?: string | number | boolean | string[];
}

export interface Operacao {
  id: string;
  metodo: Metodo;
  /** Relativo a `/api`, com parâmetros em `{chave}`. */
  caminho: string;
  titulo: string;
  descricao: string;
  /** Quando gasta crédito: em que condição. */
  cobranca?: string;
  /** Sem chave de API (dados públicos). */
  publica?: boolean;
  parametros?: Parametro[];
  /** Resposta de sucesso, como exemplo. */
  resposta?: unknown;
  /** Tipo da resposta de sucesso, quando não é JSON. */
  conteudo?: 'application/xml' | 'application/zip' | 'text/csv';
  erros?: Array<{ status: number; quando: string }>;
}

export interface Grupo {
  id: string;
  titulo: string;
  descricao: string;
  operacoes: Operacao[];
}

export const URL_BASE = 'https://baixaxml.com.br/api';

/** Completa a base de 43 dígitos com o dígito verificador (módulo 11). */
function comDigito(base: string): string {
  let soma = 0;
  let peso = 2;
  for (let i = base.length - 1; i >= 0; i--) {
    soma += Number(base[i]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  return `${base}${resto < 2 ? 0 : 11 - resto}`;
}

/** Chave de exemplo com o dígito verificador certo — a API recusa a errada. */
const CHAVE_EXEMPLO = comDigito('3524101234567800019055001000012345123456789');
const JOB_EXEMPLO = 'nfe_1728480000000_k2x9q1a';
const UUID_EXEMPLO = '6f92dd6b-5352-4c7f-a065-db421f252f4d';

const ERRO_SALDO = {
  status: 400,
  quando:
    'Saldo insuficiente (`error: "SaldoInsuficiente"`, com `faltamCentavos`).',
};

export const GRUPOS_API: Grupo[] = [
  {
    id: 'consulta',
    titulo: 'Consulta por chave',
    descricao:
      'Uma chave, o XML na resposta. Para integrar nota a nota no seu sistema.',
    operacoes: [
      {
        id: 'consultarChave',
        metodo: 'GET',
        caminho: '/nfe/consulta/{chave}',
        titulo: 'Consultar o XML de uma NF-e',
        descricao:
          'Busca o XML original (com a assinatura do emitente) pela chave de acesso. A mesma chave consultada de novo em até 24 horas não é cobrada outra vez. Com `formato=xml`, a resposta é o próprio arquivo.',
        cobranca:
          '1 XML na faixa de preço da conta, só se encontrar. O valor cobrado vem no cabeçalho `X-Cobrado-Centavos`.',
        parametros: [
          {
            nome: 'chave',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Chave de acesso, 44 dígitos.',
            exemplo: CHAVE_EXEMPLO,
          },
          {
            nome: 'formato',
            em: 'query',
            tipo: 'string',
            descricao: '`xml` para receber o arquivo cru em vez de JSON.',
          },
        ],
        resposta: {
          chave: CHAVE_EXEMPLO,
          xml: '<nfeProc xmlns="http://www.portalfiscal.inf.br/nfe" versao="4.00">…</nfeProc>',
          cobradoCentavos: 6,
          repetida: false,
        },
        erros: [
          {
            status: 400,
            quando: 'Chave com formato inválido ou dígito verificador errado.',
          },
          ERRO_SALDO,
          {
            status: 404,
            quando: 'A fonte não tem o XML (`NaoEncontrada`). Nada é cobrado.',
          },
          {
            status: 503,
            quando:
              'Fonte fora do ar (`FonteIndisponivel`), com `Retry-After`. Nada é cobrado.',
          },
        ],
      },
    ],
  },
  {
    id: 'lotes',
    titulo: 'Lotes',
    descricao:
      'Muitas chaves de uma vez. O lote roda em segundo plano: acompanhe pelo status ou receba o webhook `lote.concluido`.',
    operacoes: [
      {
        id: 'criarLote',
        metodo: 'POST',
        caminho: '/nfe/lote/chaves',
        titulo: 'Criar um lote',
        descricao:
          'Envie as chaves em texto (uma por linha, ou separadas por vírgula). Sequências de 44 dígitos são extraídas e repetidas são descartadas. O valor do lote é reservado na hora.',
        cobranca:
          'Reserva o lote inteiro na criação; no fim, cobra só os XMLs entregues e devolve o resto.',
        parametros: [
          {
            nome: 'chaves',
            em: 'body',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'As chaves, em texto livre.',
            exemplo: CHAVE_EXEMPLO,
          },
        ],
        resposta: {
          jobId: JOB_EXEMPLO,
          total: 1200,
          custoCentavos: 7200,
          status: 'PENDENTE',
        },
        erros: [
          { status: 400, quando: 'Nenhuma chave válida no texto.' },
          ERRO_SALDO,
        ],
      },
      {
        id: 'criarLoteArquivo',
        metodo: 'POST',
        caminho: '/nfe/lote',
        titulo: 'Criar um lote a partir de arquivo',
        descricao:
          'O mesmo, enviando um `.csv` ou `.txt` (até 200 MB) em `multipart/form-data`. As chaves podem estar em qualquer coluna.',
        cobranca: 'Igual ao lote por texto.',
        parametros: [
          {
            nome: 'arquivo',
            em: 'form',
            tipo: 'file',
            obrigatorio: true,
            descricao: 'CSV ou TXT com as chaves.',
          },
        ],
        resposta: {
          jobId: JOB_EXEMPLO,
          total: 85000,
          custoCentavos: 340000,
          status: 'PENDENTE',
        },
        erros: [ERRO_SALDO],
      },
      {
        id: 'listarLotes',
        metodo: 'GET',
        caminho: '/nfe/lote',
        titulo: 'Listar os lotes',
        descricao: 'Os lotes da conta, do mais recente para o mais antigo.',
        resposta: [
          {
            id: JOB_EXEMPLO,
            status: 'CONCLUIDO',
            total: 1200,
            ok: 1183,
            notfound: 17,
            erro: 0,
            createdAt: '2026-10-09T13:00:00.000Z',
            finishedAt: '2026-10-09T13:41:00.000Z',
          },
        ],
      },
      {
        id: 'statusLote',
        metodo: 'GET',
        caminho: '/nfe/lote/{jobId}',
        titulo: 'Status de um lote',
        descricao:
          '`status`: `PENDENTE`, `PROCESSANDO`, `CONCLUIDO` ou `ERRO`. `progresso` vai de 0 a 1.',
        parametros: [
          {
            nome: 'jobId',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'O `jobId` da criação.',
            exemplo: JOB_EXEMPLO,
          },
        ],
        resposta: {
          id: JOB_EXEMPLO,
          status: 'PROCESSANDO',
          total: 1200,
          ok: 640,
          notfound: 9,
          erro: 0,
          processados: 649,
          pendentes: 551,
          progresso: 0.5408,
        },
        erros: [{ status: 404, quando: 'Lote inexistente ou de outra conta.' }],
      },
      {
        id: 'resultadosLote',
        metodo: 'GET',
        caminho: '/nfe/lote/{jobId}/resultados',
        titulo: 'Resultado por chave',
        descricao:
          'O que aconteceu com cada chave já processada: `ok`, `notfound` (a fonte não tem) ou `erro` (falha que persistiu). JSON paginado, ou o arquivo inteiro com `formato=csv`.',
        parametros: [
          {
            nome: 'jobId',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'O `jobId`.',
            exemplo: JOB_EXEMPLO,
          },
          {
            nome: 'status',
            em: 'query',
            tipo: 'string',
            descricao: 'Filtra por `ok`, `notfound` ou `erro`.',
          },
          {
            nome: 'formato',
            em: 'query',
            tipo: 'string',
            descricao: '`csv` para baixar tudo (`chave;status;motivo`).',
          },
          {
            nome: 'pagina',
            em: 'query',
            tipo: 'integer',
            descricao: 'Página, a partir de 1.',
          },
          {
            nome: 'porPagina',
            em: 'query',
            tipo: 'integer',
            descricao: 'Até 1.000 (padrão 500).',
          },
        ],
        resposta: {
          jobId: JOB_EXEMPLO,
          status: 'CONCLUIDO',
          total: 1200,
          pagina: 1,
          porPagina: 500,
          itens: [
            { chave: CHAVE_EXEMPLO, status: 'ok' },
            {
              chave: '3524…',
              status: 'notfound',
              motivo: 'Nota não localizada',
            },
          ],
        },
      },
      {
        id: 'xmlDoLote',
        metodo: 'GET',
        caminho: '/nfe/lote/{jobId}/xml/{chave}',
        titulo: 'XML de uma chave do lote',
        descricao: 'Um arquivo só, sem precisar baixar o ZIP.',
        conteudo: 'application/xml',
        parametros: [
          {
            nome: 'jobId',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'O `jobId`.',
            exemplo: JOB_EXEMPLO,
          },
          {
            nome: 'chave',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Chave de acesso.',
            exemplo: CHAVE_EXEMPLO,
          },
        ],
        erros: [{ status: 404, quando: 'A chave não tem XML neste lote.' }],
      },
      {
        id: 'zipDoLote',
        metodo: 'GET',
        caminho: '/nfe/lote/{jobId}/download',
        titulo: 'ZIP do lote',
        descricao:
          'Todos os XMLs obtidos até agora — funciona também com o lote em andamento. Os arquivos ficam disponíveis por 48 horas depois do fim do lote.',
        conteudo: 'application/zip',
        parametros: [
          {
            nome: 'jobId',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'O `jobId`.',
            exemplo: JOB_EXEMPLO,
          },
        ],
        erros: [{ status: 404, quando: 'Nenhum XML ainda.' }],
      },
      {
        id: 'cancelarLote',
        metodo: 'DELETE',
        caminho: '/nfe/lote/{jobId}',
        titulo: 'Cancelar ou apagar um lote',
        descricao:
          'Cancela o lote em andamento (cobra só o que já foi entregue) ou apaga um lote concluído e os arquivos dele.',
        parametros: [
          {
            nome: 'jobId',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'O `jobId`.',
            exemplo: JOB_EXEMPLO,
          },
        ],
        resposta: { ok: true, removido: JOB_EXEMPLO },
      },
    ],
  },
  {
    id: 'sped',
    titulo: 'SPED',
    descricao:
      'Envie a EFD ICMS/IPI ou a EFD Contribuições e baixe os XMLs das notas escrituradas, com filtros. Analisar é gratuito; o download vira um lote.',
    operacoes: [
      {
        id: 'enviarSped',
        metodo: 'POST',
        caminho: '/sped/analises',
        titulo: 'Enviar arquivos SPED',
        descricao:
          '`multipart/form-data` com um ou mais TXT do SPED, ou ZIP com eles (até 300 MB cada). A análise roda em segundo plano.',
        parametros: [
          {
            nome: 'arquivos',
            em: 'form',
            tipo: 'file',
            obrigatorio: true,
            descricao: 'Os arquivos (campo repetido).',
          },
          {
            nome: 'nome',
            em: 'form',
            tipo: 'string',
            descricao: 'Nome para identificar a análise.',
          },
        ],
        resposta: { id: UUID_EXEMPLO, status: 'PROCESSANDO' },
      },
      {
        id: 'verSped',
        metodo: 'GET',
        caminho: '/sped/analises/{id}',
        titulo: 'Situação e resumo da análise',
        descricao:
          'Quantidade de notas, período e a situação (`PROCESSANDO`, `CONCLUIDA`, `ERRO`).',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id da análise.',
            exemplo: UUID_EXEMPLO,
          },
        ],
      },
      {
        id: 'baixarSped',
        metodo: 'POST',
        caminho: '/sped/analises/{id}/baixar',
        titulo: 'Baixar os XMLs das notas do SPED',
        descricao:
          'Cria um lote com as chaves das notas que atendem aos filtros (todos opcionais). A partir daí é um lote comum.',
        cobranca: 'Igual ao lote.',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id da análise.',
            exemplo: UUID_EXEMPLO,
          },
          {
            nome: 'competencias',
            em: 'body',
            tipo: 'array',
            descricao: '`AAAA` ou `AAAA-MM`.',
          },
          {
            nome: 'cfops',
            em: 'body',
            tipo: 'array',
            descricao: 'CFOPs, 4 dígitos.',
          },
          {
            nome: 'operacoes',
            em: 'body',
            tipo: 'array',
            descricao: '`E` (entrada) e/ou `S` (saída).',
          },
          {
            nome: 'emissoes',
            em: 'body',
            tipo: 'array',
            descricao: '`P` (própria) e/ou `T` (terceiros).',
          },
          {
            nome: 'modelos',
            em: 'body',
            tipo: 'array',
            descricao: 'Ex.: `55`.',
          },
          {
            nome: 'situacoes',
            em: 'body',
            tipo: 'array',
            descricao: 'Código de situação do SPED, 2 dígitos.',
          },
        ],
        resposta: { jobId: JOB_EXEMPLO, total: 5508, custoCentavos: 33048 },
        erros: [ERRO_SALDO],
      },
    ],
  },
  {
    id: 'captura',
    titulo: 'Captura automática (certificado A1)',
    descricao:
      'As notas emitidas contra os CNPJs da sua conta, direto da SEFAZ. Certificados e empresas são cadastrados no painel; pela API você lê as notas. Em liberação gradual.',
    operacoes: [
      {
        id: 'planoCaptura',
        metodo: 'GET',
        caminho: '/dfe/plano',
        titulo: 'Plano da captura',
        descricao:
          'Faixa paga, CNPJs em uso, data e valor da renovação, e quanto custaria ativar mais um CNPJ agora.',
      },
      {
        id: 'empresasCaptura',
        metodo: 'GET',
        caminho: '/dfe/empresas',
        titulo: 'Empresas monitoradas',
        descricao:
          'Situação de cada CNPJ, última consulta à SEFAZ, certificado e contagem de notas.',
      },
      {
        id: 'alteracoesNotas',
        metodo: 'GET',
        caminho: '/dfe/notas/alteracoes',
        titulo: 'Notas novas ou alteradas',
        descricao:
          'Leitura incremental: guarde o `cursor` e mande na próxima chamada para receber só o que mudou (nota nova, XML completo que chegou, cancelamento). Repita enquanto `temMais` for `true`.',
        parametros: [
          {
            nome: 'cursor',
            em: 'query',
            tipo: 'string',
            descricao: 'O da resposta anterior. Vazio = desde o começo.',
          },
          {
            nome: 'empresaId',
            em: 'query',
            tipo: 'string',
            descricao: 'Só desta empresa.',
          },
          {
            nome: 'limite',
            em: 'query',
            tipo: 'integer',
            descricao: 'Até 500 (padrão 100).',
          },
        ],
        resposta: {
          itens: [
            {
              id: UUID_EXEMPLO,
              chave: CHAVE_EXEMPLO,
              emitenteDoc: '12345678000190',
              emitenteNome: 'FORNECEDOR SA',
              valor: '1500.50',
              dataEmissao: '2026-10-08T14:22:00.000Z',
              situacao: 'AUTORIZADA',
              xmlCompleto: true,
              disponivel: true,
              manifestacao: 'CIENCIA',
              empresa: { cnpj: '98765432000110', nome: 'Cliente Ltda' },
              alteradaEm: '2026-10-09T10:01:12.000Z',
            },
          ],
          cursor: 'MjAyNi0xMC0wOVQxMDowMToxMi4wMDBafDZmOTI…',
          temMais: false,
        },
      },
      {
        id: 'listarNotasCaptura',
        metodo: 'GET',
        caminho: '/dfe/notas',
        titulo: 'Buscar notas recebidas',
        descricao: 'Listagem paginada com filtros, como no painel.',
        parametros: [
          {
            nome: 'empresaId',
            em: 'query',
            tipo: 'string',
            descricao: 'Empresa.',
          },
          {
            nome: 'de',
            em: 'query',
            tipo: 'string',
            descricao: 'Emissão a partir de (`AAAA-MM-DD`).',
          },
          {
            nome: 'ate',
            em: 'query',
            tipo: 'string',
            descricao: 'Emissão até.',
          },
          {
            nome: 'situacao',
            em: 'query',
            tipo: 'string',
            descricao: '`AUTORIZADA`, `CANCELADA` ou `DENEGADA`.',
          },
          {
            nome: 'busca',
            em: 'query',
            tipo: 'string',
            descricao: 'Emitente, CNPJ ou chave.',
          },
          {
            nome: 'pagina',
            em: 'query',
            tipo: 'integer',
            descricao: 'Página.',
          },
          {
            nome: 'porPagina',
            em: 'query',
            tipo: 'integer',
            descricao: 'Itens por página.',
          },
        ],
      },
      {
        id: 'xmlCaptura',
        metodo: 'GET',
        caminho: '/dfe/notas/{id}/xml',
        titulo: 'XML de uma nota recebida',
        descricao: 'O XML completo, quando já chegou da SEFAZ.',
        conteudo: 'application/xml',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id da nota (não a chave).',
            exemplo: UUID_EXEMPLO,
          },
        ],
        erros: [
          { status: 400, quando: 'Só o resumo chegou até agora.' },
          { status: 404, quando: 'Nota inexistente ou de outra conta.' },
        ],
      },
      {
        id: 'zipCaptura',
        metodo: 'GET',
        caminho: '/dfe/notas/zip',
        titulo: 'ZIP das notas recebidas',
        descricao: 'Os XMLs completos que atendem aos mesmos filtros da busca.',
        conteudo: 'application/zip',
        parametros: [
          {
            nome: 'empresaId',
            em: 'query',
            tipo: 'string',
            descricao: 'Empresa.',
          },
          {
            nome: 'de',
            em: 'query',
            tipo: 'string',
            descricao: 'Emissão a partir de.',
          },
          {
            nome: 'ate',
            em: 'query',
            tipo: 'string',
            descricao: 'Emissão até.',
          },
        ],
      },
    ],
  },
  {
    id: 'webhooks',
    titulo: 'Webhooks',
    descricao:
      'Avisamos uma URL sua quando algo acontece, em vez de você consultar em intervalos. Veja a seção "Recebendo webhooks" para validar a assinatura.',
    operacoes: [
      {
        id: 'eventosWebhook',
        metodo: 'GET',
        caminho: '/integracoes/webhooks/eventos',
        titulo: 'Eventos disponíveis',
        descricao: 'Os nomes que podem ser assinados.',
        resposta: [
          'lote.concluido',
          'captura.xml_disponivel',
          'captura.nota_cancelada',
        ],
      },
      {
        id: 'listarWebhooks',
        metodo: 'GET',
        caminho: '/integracoes/webhooks',
        titulo: 'Listar webhooks',
        descricao: 'Os endpoints cadastrados e a última entrega de cada um.',
      },
      {
        id: 'criarWebhook',
        metodo: 'POST',
        caminho: '/integracoes/webhooks',
        titulo: 'Cadastrar um webhook',
        descricao:
          'URL `https://` pública e os eventos. O `segredo` de assinatura aparece só nesta resposta — guarde. Até 5 por conta.',
        parametros: [
          {
            nome: 'url',
            em: 'body',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Onde entregar.',
            exemplo: 'https://seu-sistema.com.br/webhooks/baixaxml',
          },
          {
            nome: 'eventos',
            em: 'body',
            tipo: 'array',
            obrigatorio: true,
            descricao: 'Um ou mais eventos.',
            exemplo: ['lote.concluido'],
          },
        ],
        resposta: {
          id: UUID_EXEMPLO,
          url: 'https://seu-sistema.com.br/webhooks/baixaxml',
          eventos: ['lote.concluido'],
          ativo: true,
          segredo: 'whsec_…',
        },
        erros: [
          {
            status: 400,
            quando:
              'URL que não é https, de rede interna, evento desconhecido ou limite atingido.',
          },
        ],
      },
      {
        id: 'atualizarWebhook',
        metodo: 'PATCH',
        caminho: '/integracoes/webhooks/{id}',
        titulo: 'Alterar um webhook',
        descricao: 'Troca URL ou eventos, ou pausa com `ativo: false`.',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id do webhook.',
            exemplo: UUID_EXEMPLO,
          },
          { nome: 'url', em: 'body', tipo: 'string', descricao: 'Nova URL.' },
          {
            nome: 'eventos',
            em: 'body',
            tipo: 'array',
            descricao: 'Novos eventos.',
          },
          {
            nome: 'ativo',
            em: 'body',
            tipo: 'boolean',
            descricao: 'Pausar/retomar.',
          },
        ],
      },
      {
        id: 'removerWebhook',
        metodo: 'DELETE',
        caminho: '/integracoes/webhooks/{id}',
        titulo: 'Remover um webhook',
        descricao: 'Entregas pendentes dele são descartadas.',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id do webhook.',
            exemplo: UUID_EXEMPLO,
          },
        ],
      },
      {
        id: 'testarWebhook',
        metodo: 'POST',
        caminho: '/integracoes/webhooks/{id}/teste',
        titulo: 'Enviar um evento de teste',
        descricao:
          'Entrega um evento `teste` agora e devolve o resultado (status HTTP da sua URL ou o erro).',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id do webhook.',
            exemplo: UUID_EXEMPLO,
          },
        ],
      },
      {
        id: 'novoSegredoWebhook',
        metodo: 'POST',
        caminho: '/integracoes/webhooks/{id}/segredo',
        titulo: 'Gerar outro segredo',
        descricao:
          'Para rotação ou se o segredo vazou. O antigo deixa de valer na hora.',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id do webhook.',
            exemplo: UUID_EXEMPLO,
          },
        ],
      },
      {
        id: 'entregasWebhook',
        metodo: 'GET',
        caminho: '/integracoes/webhooks/{id}/entregas',
        titulo: 'Últimas entregas',
        descricao:
          'As 50 mais recentes, com situação (`entregue`, `pendente`, `falhou`), tentativas e o último erro.',
        parametros: [
          {
            nome: 'id',
            em: 'path',
            tipo: 'string',
            obrigatorio: true,
            descricao: 'Id do webhook.',
            exemplo: UUID_EXEMPLO,
          },
        ],
      },
    ],
  },
  {
    id: 'conta',
    titulo: 'Conta e créditos',
    descricao: 'Saldo, preços e extrato.',
    operacoes: [
      {
        id: 'saldo',
        metodo: 'GET',
        caminho: '/creditos',
        titulo: 'Saldo',
        descricao:
          'Disponível, reservado em lotes em andamento e a tabela de preços.',
      },
      {
        id: 'orcamento',
        metodo: 'GET',
        caminho: '/creditos/orcamento',
        titulo: 'Quanto custa',
        descricao:
          'O preço por XML e o total para N chaves, já com o volume dos últimos 30 dias da conta, e se o saldo cobre.',
        parametros: [
          {
            nome: 'chaves',
            em: 'query',
            tipo: 'integer',
            obrigatorio: true,
            descricao: 'Quantidade de chaves.',
            exemplo: 1200,
          },
        ],
        resposta: {
          chaves: 1200,
          volumeRecente: 8400,
          precoUnitarioCentavos: 6,
          totalCentavos: 7200,
          saldoCentavos: 25000,
          suficiente: true,
          faltamCentavos: 0,
        },
      },
      {
        id: 'precos',
        metodo: 'GET',
        caminho: '/creditos/precos',
        titulo: 'Tabela de preços',
        descricao: 'As faixas por volume. Pública.',
        publica: true,
      },
      {
        id: 'extrato',
        metodo: 'GET',
        caminho: '/creditos/extrato',
        titulo: 'Extrato',
        descricao:
          'Recargas, reservas, consumos e devoluções, com o saldo depois de cada um.',
      },
    ],
  },
];

/** Todas as operações, na ordem da página. */
export const OPERACOES_API: Operacao[] = GRUPOS_API.flatMap((g) => g.operacoes);
